import { Injectable } from '@angular/core'
import { GrayLogService } from '@app/cadastro/admin/services/graylog/graylog.service'
import { LexTranslateService } from './lex-translate.service'
import { environment } from 'src/environments/environment'
import { LanguageEnum } from '../models/LanguageEnum'

@Injectable({
  providedIn: 'root'
})
export class PrivallyService {

  private _pid: string

  constructor(
    private grayLogService: GrayLogService,
    private lexTranslateService: LexTranslateService
  ) {
    this._pid = (environment.P_ID_PRIVALLY || '').trim()
  }

  init() {
    if (!this._pid) {
      console.log("P_ID_PRIVALLY not provided, Privally Service won't start.")
      return
    }

    if (document.getElementById('PrivallyApp')) {
      console.log('Privally script has already been loaded.')
      return
    }

    // Create script element for Privally
    const privallyScript = document.createElement('script')
    privallyScript.type = 'text/javascript'
    privallyScript.id = 'PrivallyApp'
    privallyScript.src = 'https://app.privally.global/app.js'
    privallyScript.setAttribute('pid', this._pid)

    // Set language attribute based on current tenant language
    const lang = this.getCurrentLanguage()
    if (lang) {
      privallyScript.setAttribute('lang', lang)
    }

    privallyScript.async = true

    // Insert at the beginning of body
    const firstChild = document.body.firstChild
    if (firstChild) {
      document.body.insertBefore(privallyScript, firstChild)
    } else {
      document.body.appendChild(privallyScript)
    }

    console.log('Privally script loaded successfully.')

    // Update HTML lang attribute
    this.updateHtmlLang()

    // Subscribe to language changes
    this.subscribeToLanguageChanges()
  }

  /**
   * Remove e recarrega o script Privally para aplicar mudanças de idioma
   */
  reloadScript(): void {
    // Remove o script existente
    const existingScript = document.getElementById('PrivallyApp')
    if (existingScript) {
      existingScript.remove()
      console.log('Privally script removed.')
    }

    // Remove a interface do Privally (se existir)
    const privallyApp = document.getElementById('oPrivallyApp')
    if (privallyApp) {
      privallyApp.remove()
      console.log('Privally UI element removed.')
    }

    // Recarrega o script com as novas configurações (novo idioma do cookie)
    this.init()
  }

  /**
   * Atualiza o atributo lang do elemento HTML
   */
  updateHtmlLang(): void {
    const lang = this.getCurrentLanguage()
    if (lang) {
      // Map Privally language codes to standard HTML lang codes
      const htmlLang = lang === 'eng' ? 'en' : lang
      document.documentElement.lang = htmlLang
      console.log(`HTML lang attribute updated to: ${htmlLang}`)
    }
  }

  /**
   * Obtém o idioma atual baseado no tenant
   * @returns Código do idioma para o Privally ('pt' ou 'eng')
   */
  private getCurrentLanguage(): string | null {
    const currentTenant = this.lexTranslateService.currentTenant.value
    if (!currentTenant) {
      return null
    }

    // Map LanguageEnum to Privally language codes
    switch (currentTenant.language) {
      case LanguageEnum.PT_BR:
        return 'pt'
      case LanguageEnum.EN_US:
        return 'eng'
      default:
        return 'pt' // Default to Portuguese
    }
  }

  /**
   * Inscreve-se para mudanças de idioma do tenant
   */
  private subscribeToLanguageChanges(): void {
    this.lexTranslateService.currentTenant.subscribe((tenant) => {
      if (tenant && document.getElementById('PrivallyApp')) {
        const currentLang = this.getCurrentLanguage()
        const scriptElement = document.getElementById('PrivallyApp')
        const scriptLang = scriptElement?.getAttribute('lang')

        // Only reload if language actually changed
        if (scriptLang && currentLang && scriptLang !== currentLang) {
          console.log(`Language changed from ${scriptLang} to ${currentLang}. Reloading Privally script.`)
          this.reloadScript()
        } else if (currentLang) {
          // Update HTML lang even if script doesn't need reload
          this.updateHtmlLang()
        }
      }
    })
  }
}
